import {
    type FocusEvent,
    type KeyboardEvent,
    type MouseEvent,
    useCallback,
} from "react";
import type { RowData } from "@tanstack/react-table";
import { isInsideInteractiveCellContent } from "../cells/cellControls";
import { useListCellClick } from "../editing/list/useListCellClick";
import type { DataGridInstance } from "../types";
import { isEditableTarget } from "./editableTarget";
import {
    getFocusLevel,
    handFocusToSoleControl,
    isInsideCellControls,
    isSoleControl,
} from "./focusLevel";
import { runGridAction } from "./gridActions";
import type { GridController } from "./gridController";
import { runHeaderKey } from "./headerKeys";
import { openRow } from "./leaveActions";
import { syncListFocus } from "./listActions";
import { endSelectionRunOffRow } from "./selectionRun";
import { focusTableRow } from "./useAutoFocus";
import { useControlPopupEscape } from "./useControlPopupEscape";
import { isFocusRevealingKey, useFocusQuiet } from "./useFocusQuiet";
import { isNativeActivation, normalizeKey, resolveGridKey } from "./keyMap";
import {
    findFirstFocusableRow,
    getFocusedAddress,
    getRowModeColumnId,
    isCellFocusTarget,
} from "./navigation";

const closestData = (target: EventTarget | null, attribute: string) => {
    if (!(target instanceof Element)) {
        return null;
    }
    return target.closest<HTMLElement>(`[${attribute}]`);
};

const isInsideHeader = (target: EventTarget | null) =>
    closestData(target, "data-dt-header") !== null;

const isOpenTrigger = (target: EventTarget | null) =>
    target instanceof Element &&
    target.getAttribute("aria-expanded") === "true";

/**
 * A one-control cell's control keeps only Enter and Space (and every key
 * while its popup is open); the grid takes its other keys first, in the
 * capture phase, before a menu trigger opens on ArrowDown.
 */
const isGridFirstKey = (event: KeyboardEvent<HTMLDivElement>) =>
    isSoleControl(event.target) &&
    !isOpenTrigger(event.target) &&
    !isNativeActivation(event);

interface UseGridKeyboardInput<TData extends RowData> {
    instance: DataGridInstance<TData>;
    controller: GridController;
    /** Runs first for body keys (editing); returning true marks the key handled. */
    onCellKey?: (event: KeyboardEvent<HTMLDivElement>) => boolean;
}

/**
 * The grid's single set of delegated handlers: one keydown for every grid
 * key, focusin to sync focus state with clicks and Tab, and click to open
 * rows. Interactive cell content keeps its own keys and clicks.
 */
export const useGridKeyboard = <TData extends RowData>({
    instance,
    controller,
    onCellKey,
}: UseGridKeyboardInput<TData>) => {
    const { table } = instance;
    const listClick = useListCellClick({ instance, controller });
    useFocusQuiet({
        controller,
        navigation: instance.hasCellNavigation ? "cell" : "row",
    });

    const returnFromControls = useCallback(() => {
        const current = getFocusedAddress(table.atoms.cellSelection.get());
        const isCellTarget = isCellFocusTarget(instance);
        if (current && controller.rowIndexById.has(current.rowId)) {
            controller.focusTarget({
                rowId: current.rowId,
                columnId: isCellTarget ? current.columnId : null,
            });
        }
    }, [controller, instance, table]);
    useControlPopupEscape({ controller, returnFromControls });

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            const isOwnTarget =
                event.target instanceof Node &&
                event.currentTarget.contains(event.target);
            // A keyboard drag owns every key until it drops or cancels.
            if (
                !isOwnTarget ||
                event.defaultPrevented ||
                isEditableTarget(event.target) ||
                controller.dragSource.get() !== null
            ) {
                return;
            }
            if (
                isInsideCellControls(event.target) &&
                (!isSoleControl(event.target) || isOpenTrigger(event.target))
            ) {
                // An open trigger's Escape belongs to its popup; focus
                // returns once it closes (useControlPopupEscape).
                if (event.key === "Escape" && !isOpenTrigger(event.target)) {
                    event.preventDefault();
                    event.stopPropagation();
                    returnFromControls();
                }
                return;
            }
            if (isInsideHeader(event.target)) {
                if (runHeaderKey({ event, instance, controller })) {
                    event.preventDefault();
                }
                return;
            }
            if (isNativeActivation(event)) {
                return;
            }
            if (onCellKey?.(event)) {
                event.preventDefault();
                return;
            }
            const action = resolveGridKey(normalizeKey(event));
            if (
                action &&
                runGridAction({
                    action,
                    instance,
                    controller,
                    target: event.target,
                })
            ) {
                event.preventDefault();
                // Host Escape handlers (closing a drawer) wait until the
                // ladder leaves the table.
                if (action.type === "escape") {
                    event.stopPropagation();
                }
            }
        },
        [controller, instance, onCellKey, returnFromControls],
    );

    const runKeys = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            const wasPrevented = event.defaultPrevented;
            handleKeyDown(event);
            if (!wasPrevented && isFocusRevealingKey(event)) {
                controller.setFocusQuiet(null);
            }
        },
        [controller, handleKeyDown],
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (!isGridFirstKey(event)) {
                runKeys(event);
            }
        },
        [runKeys],
    );

    const onKeyDownCapture = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (isGridFirstKey(event)) {
                runKeys(event);
                if (event.defaultPrevented) {
                    event.stopPropagation();
                }
            }
        },
        [runKeys],
    );

    const onFocus = useCallback(
        (event: FocusEvent<HTMLDivElement>) => {
            controller.isFocusWithin.set(true);
            const level = getFocusLevel(event.target, event.currentTarget);
            if (level !== null) {
                controller.setFocusLevel(level);
            }
            const rowElement = closestData(event.target, "data-row-id");
            const rowId = rowElement?.dataset.rowId;
            endSelectionRunOffRow(controller, rowId);
            if (event.target === event.currentTarget) {
                const firstRow = findFirstFocusableRow(
                    table.getRowModel().rows,
                    instance.rowKinds.isSeparator,
                );
                if (firstRow) {
                    focusTableRow({ instance, controller, rowId: firstRow.id });
                }
                return;
            }
            if (!rowElement || !rowId) {
                return;
            }
            if (
                instance.hasListEditing &&
                !syncListFocus({
                    instance,
                    controller,
                    rowElement,
                    target: event.target,
                })
            ) {
                return;
            }
            if (handFocusToSoleControl(event.target, event.relatedTarget)) {
                return;
            }
            const cellColumnId = closestData(event.target, "data-column-id")
                ?.dataset.columnId;
            const current = getFocusedAddress(table.atoms.cellSelection.get());
            const columnId = isCellFocusTarget(instance)
                ? (cellColumnId ??
                  current?.columnId ??
                  getRowModeColumnId(table))
                : (current?.columnId ?? getRowModeColumnId(table));
            if (
                columnId !== null &&
                (current?.rowId !== rowId || current.columnId !== columnId)
            ) {
                table.setFocusedCell(rowId, columnId);
            }
        },
        [controller, instance, table],
    );

    const onBlur = useCallback(
        (event: FocusEvent<HTMLDivElement>) => {
            const next = event.relatedTarget;
            if (next instanceof Node && !event.currentTarget.contains(next)) {
                controller.isFocusWithin.set(false);
            }
        },
        [controller],
    );

    const onClick = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            if (
                isInsideInteractiveCellContent(
                    event.target,
                    event.currentTarget,
                )
            ) {
                return;
            }
            const rowId = closestData(event.target, "data-row-id")?.dataset
                .rowId;
            const columnId = closestData(event.target, "data-column-id")
                ?.dataset.columnId;
            if (rowId && columnId && listClick.clickCell({ rowId, columnId })) {
                return;
            }
            if (instance.hasRangeSelection) {
                return;
            }
            const row = rowId ? table.getRowModel().rowsById[rowId] : undefined;
            if (row && !instance.rowKinds.isSeparator(row.original)) {
                openRow({ instance, row, columnId });
            }
        },
        [instance, listClick, table],
    );

    return {
        onKeyDown,
        onKeyDownCapture,
        onFocus,
        onBlur,
        onClick,
        onPointerDown: listClick.onPointerDown,
    };
};
